<template>
    <div class="animated fadeIn nsp">
        <div v-if="nspanelDenied" class="text-center text-muted py-5">
            {{ $t('NSP_NO_ACCESS') }}
        </div>

        <div v-else-if="!nspanelLoaded" class="text-center text-muted py-5">
            {{ $t('NSP_LOADING') }}
        </div>

        <b-nav v-if="nspanelLoaded && buildingTabs.length > 1" pills class="mb-2">
            <b-nav-item
                v-for="(tab, index) in buildingTabs"
                :key="index"
                :active="index === activeBuildingTab"
                v-on:click="activeBuildingTab = index">{{tab.name}}</b-nav-item>
        </b-nav>

        <b-tabs v-if="nspanelLoaded" v-model="activeTab" content-class="mt-3">
            <b-tab v-if="hasClimate" :title="$t('NSP_TAB_CLIMATE')">
                <div class="row">
                    <div class="col-12 col-xl-4 mb-3" v-for="one in acsHere" :key="one.moduleId">
                        <ac-card
                            :name="one.moduleName"
                            :ac="nspanel.ac"
                            :ac-temp="nspanel.acTemp"
                            v-on:power="setAcPower"
                            v-on:mode="setAcMode"/>
                    </div>
                    <div class="col-12 col-xl-5 mb-3" v-if="here(hvac.recuperator)">
                        <recuperator-card
                            :ac="nspanel.ac"
                            v-on:power="setRecoupPower"
                            v-on:season="setRecoupSeason"
                            v-on:mode="setRecoupMode"
                            v-on:fans="setRecoupFans"
                            v-on:roomtemp="setRecoupRoomTemp"/>
                    </div>
                    <div class="col-12 col-xl-3 mb-3" v-if="here(hvac.recuperator) || here(hvac.exhaust)">
                        <ventilation-card :vent="nspanel.vent"/>
                    </div>
                </div>

                <!-- a house can have several of either, so each gets its own card
                     rather than the first one standing for all of them -->
                <div class="row">
                    <div class="col-12 col-xl-6 mb-3"
                        v-for="station in weatherStations"
                        :key="station.moduleId">
                        <weather-card :module="station">
                            <template v-slot:footer>
                                <sensor-history :module-id="station.moduleId"/>
                            </template>
                        </weather-card>
                    </div>
                    <div class="col-12 col-xl-6 mb-3"
                        v-for="sensor in indoorSensors"
                        :key="sensor.moduleId">
                        <indoor-card :module="sensor">
                            <template v-slot:footer>
                                <sensor-history :module-id="sensor.moduleId"/>
                            </template>
                        </indoor-card>
                    </div>
                </div>
            </b-tab>

            <b-tab v-if="rooms.length > 0" :title="$t('NSP_TAB_ROOMS')">
                <div class="row">
                    <div
                        class="col-12 col-md-6 col-xl-4 mb-3"
                        v-for="room in rooms"
                        :key="room.moduleId">
                        <room-card
                            :room="room"
                            v-on:thermostat="setRoomThermostat"
                            v-on:actemp="setRoomAcTemp"
                            v-on:boost="setRoomBoost"
                            v-on:light-toggle="toggleLight"
                            v-on:light-dim="dimLight"
                            v-on:light-color="colorLight"
                            v-on:light-effect="effectLight"
                            v-on:shutter-up="shutterUp"
                            v-on:shutter-stop="shutterStop"
                            v-on:shutter-down="shutterDown"/>
                    </div>
                </div>
            </b-tab>

            <b-tab v-if="rooms.length > 0" :title="$t('NSP_TAB_PANELS')">
                <b-table
                    striped
                    small
                    :items="panelRows"
                    :fields="panelFields"/>
            </b-tab>
        </b-tabs>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Api from '@/api/NsPanel';
import AcCard from '@/components/nspanel/AcCard.vue';
import RecuperatorCard from '@/components/nspanel/RecuperatorCard.vue';
import VentilationCard from '@/components/nspanel/VentilationCard.vue';
import RoomCard from '@/components/nspanel/RoomCard.vue';
import WeatherCard from '@/components/lib/WeatherCard.vue';
import IndoorCard from '@/components/lib/IndoorCard.vue';
import SensorHistory from '@/components/lib/SensorHistory.vue';

// page names as tracked in NSPanel.panelPages on the server
const PAGE_LABELS = {
    screensaver: 'NSP_PAGE_SCREENSAVER',
    thermostat: 'NSP_PAGE_THERMOSTAT',
    ac: 'NSP_PAGE_AC',
    vent: 'NSP_PAGE_VENT',
    page: 'NSP_PAGE_ENTITIES',
};

export default
{
    name: 'Home',
    components:
    {
        'ac-card': AcCard,
        'recuperator-card': RecuperatorCard,
        'ventilation-card': VentilationCard,
        'room-card': RoomCard,
        'weather-card': WeatherCard,
        'indoor-card': IndoorCard,
        'sensor-history': SensorHistory,
    },
    data()
    {
        return {
            // held here so a state refresh does not throw the user back to the
            // first tab, which is what happens when b-tab carries `active` and the
            // whole nspanel object is replaced underneath it
            activeTab: 0,
            activeBuildingTab: 0,
        };
    },
    created()
    {
        const me = this;

        me.fetchNsPanel();

        me.onHvac = (message) =>
        {
            me.$store.commit('NSPANEL_HVAC', message);
        };

        me.onModule = (message) =>
        {
            me.$store.commit('NSPANEL_MODULE', message);
        };

        me.socket.on('nspanel', me.onHvac);
        me.socket.on('module', me.onModule);

        // there is no polling here. The 'nspanel' event carries the hvac state and
        // the 'module' event carries a full module row for anything else that
        // changes, so a periodic full refetch only cost a round trip and made the
        // page jump.
    },
    beforeDestroy()
    {
        const me = this;

        if (me.socket)
        {
            me.socket.off('nspanel', me.onHvac);
            me.socket.off('module', me.onModule);
        }
    },
    methods:
    {
        // whether one device belongs under the building that is open. Null means
        // the caller has no rights to it, or it is not there at all.
        here(device)
        {
            const me = this;

            if (device == null)
            {
                return false;
            }

            const own = (device.buildingId == null) ? null : parseInt(device.buildingId, 10);

            return (own === me.activeBuildingId);
        },
        // a room stands where its panel stands, a card where its module does
        inBuilding(list)
        {
            const me = this;

            if (!Array.isArray(list))
            {
                return [];
            }

            return list.filter((one) =>
            {
                const own = (one.buildingId == null) ? null : parseInt(one.buildingId, 10);

                return (own === me.activeBuildingId);
            });
        },
        ...mapActions([
            'fetchNsPanel',
        ]),
        handle(val, refetch)
        {
            const me = this;

            if (val && val.error)
            {
                if (String(val.notice || '').indexOf('no_privilage') !== -1)
                {
                    me.$store.commit('NSPANEL_DENIED');
                    return;
                }

                me.$notify.notice(val);
                // server refused the change, pull the real state back
                me.fetchNsPanel();
                return;
            }

            if (val && val.ac)
            {
                me.$store.commit('NSPANEL_AC_PATCH', val.ac);
            }
            else if (refetch != null)
            {
                me.fetchNsPanel();
            }
        },
        setAcPower(on)
        {
            const me = this;

            me.$store.commit('NSPANEL_AC_PATCH', { on });
            Api.setAcPower({ on }, (val) => me.handle(val));
        },
        setAcMode(mode)
        {
            const me = this;

            me.$store.commit('NSPANEL_AC_PATCH', { mode });
            Api.setAcMode({ mode }, (val) => me.handle(val));
        },
        setRecoupPower(on)
        {
            const me = this;

            me.$store.commit('NSPANEL_AC_PATCH', { recoupUserOn: on });
            Api.setRecoupPower({ on }, (val) => me.handle(val));
        },
        setRecoupSeason(season)
        {
            const me = this;

            me.$store.commit('NSPANEL_AC_PATCH', { recoupSeason: season });
            Api.setRecoupSeason({ season }, (val) => me.handle(val));
        },
        setRecoupMode(mode)
        {
            const me = this;

            me.$store.commit('NSPANEL_AC_PATCH', { recoupWantedMode: mode });
            Api.setRecoupMode({ mode }, (val) => me.handle(val));
        },
        setRecoupFans(fans)
        {
            const me = this;

            me.$store.commit('NSPANEL_AC_PATCH', { fanSupplySet: fans.supply, fanReturnSet: fans.ret });
            Api.setRecoupFans(fans, (val) => me.handle(val));
        },
        setRecoupRoomTemp(temperature)
        {
            const me = this;

            me.$store.commit('NSPANEL_AC_PATCH', { roomTemp: temperature });
            Api.setRecoupRoomTemp({ temperature }, (val) => me.handle(val));
        },
        setRoomThermostat(params)
        {
            const me = this;

            Api.setRoomThermostat(params, (val) => me.handle(val, true));
        },
        setRoomBoost(params)
        {
            const me = this;

            Api.setRoomBoost(params, (val) => me.handle(val, true));
        },
        setRoomAcTemp(params)
        {
            const me = this;

            Api.setRoomAcTemp(params, (val) => me.handle(val, true));
        },
        toggleLight(entity)
        {
            const me = this;

            if (entity.type === 'dimmer' || entity.type === 'rgb')
            {
                Api.setDimmer({ moduleId: entity.moduleId, power: entity.on ? 0 : 1 }, (val) => me.handle(val));
            }
            else
            {
                Api.activateDevice({ moduleId: entity.moduleId }, (val) => me.handle(val));
            }
        },
        dimLight(payload)
        {
            const me = this;

            Api.setDimmer(
                { moduleId: payload.entity.moduleId, brightness: payload.brightness },
                (val) => me.handle(val),
            );
        },
        effectLight(payload)
        {
            const me = this;

            // setDimmer stores it on the module, so the strip keeps the effect
            // when a rule switches it on later
            Api.setDimmer(
                { moduleId: payload.entity.moduleId, effect: payload.effect },
                (val) => me.handle(val),
            );
        },
        colorLight(payload)
        {
            const me = this;

            Api.setDimmer(
                {
                    moduleId: payload.entity.moduleId,
                    ledcolor: { r: payload.ledcolor.r, g: payload.ledcolor.g, b: payload.ledcolor.b },
                },
                (val) => me.handle(val),
            );
        },
        shutterUp(entity)
        {
            const me = this;

            Api.activateDevice({ moduleId: entity.moduleId }, (val) => me.handle(val));
        },
        shutterStop(entity)
        {
            const me = this;

            Api.stopDevice({ moduleId: entity.moduleId }, (val) => me.handle(val));
        },
        shutterDown(entity)
        {
            const me = this;

            Api.activateSecDevice({ moduleId: entity.moduleId }, (val) => me.handle(val));
        },
    },
    computed:
    {
        /**
         * One tab per building plus unclaimed, which is a real place to be and
         * not a gap: a module nobody has assigned still has to be reachable.
         */
        hvac()
        {
            return (this.nspanel && this.nspanel.hvac) ? this.nspanel.hvac : {};
        },
        /**
         * The climate tab is worth showing when at least one thing on it is. The
         * ac, the recuperator and the extra fan are three separate devices and a
         * house may have none of them.
         */
        /**
         * The air conditioners that belong under the tab that is open. They are
         * found by device type on the server, so this follows whatever is
         * marked as one rather than a module id written into the code.
         */
        acsHere()
        {
            const me = this;

            // newer builds send a list, older ones sent a single ac. Both are
            // read so the api and this page can be deployed in either order.
            let list = [];

            if (Array.isArray(me.hvac.acs))
            {
                list = me.hvac.acs;
            }
            else if (me.hvac.ac != null)
            {
                list = [me.hvac.ac];
            }

            return list.filter((one) => me.here(one));
        },
        hasClimate()
        {
            const me = this;

            return (me.acsHere.length > 0
                || me.here(me.hvac.recuperator)
                || me.here(me.hvac.exhaust)
                || me.weatherStations.length > 0
                || me.indoorSensors.length > 0);
        },
        buildingTabs()
        {
            const me = this;
            const list = (me.nspanel && me.nspanel.buildings) ? me.nspanel.buildings : [];

            return [...list, { buildingId: null, name: me.$t('UNCLAIMED') }];
        },
        activeBuildingId()
        {
            const tab = this.buildingTabs[this.activeBuildingTab];

            return (tab == null) ? null : tab.buildingId;
        },
        weatherStations()
        {
            return this.inBuilding(this.nspanel.weatherStations);
        },
        indoorSensors()
        {
            return this.inBuilding(this.nspanel.indoorSensors);
        },
        rooms()
        {
            return this.inBuilding(this.nspanel.rooms);
        },
        ...mapGetters([
            'socket',
            'nspanel',
            'nspanelLoaded',
            'nspanelDenied',
        ]),
        panelFields()
        {
            const me = this;

            return [
                { key: 'moduleName', label: me.$t('NSP_PANEL') },
                { key: 'moduleMac', label: me.$t('NSP_MAC') },
                { key: 'temperature', label: me.$t('NSP_TEMPERATURE') },
                { key: 'currentPage', label: me.$t('NSP_CURRENT_PAGE') },
            ];
        },
        panelRows()
        {
            const me = this;

            // the rooms of the building that is open, not every panel there is -
            // a panel in the other house has no business on this tab
            return me.rooms.map((room) => (
                {
                    moduleName: room.moduleName,
                    moduleMac: room.moduleMac,
                    temperature: `${room.temperature} °C`,
                    currentPage: PAGE_LABELS[room.currentPage] ? me.$t(PAGE_LABELS[room.currentPage]) : '-',
                }));
        },
    },
};
</script>
